<template>
    <div class="product-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && loanProduct.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="loanProduct"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                    </div>
                </div>

                <div v-if="editMode" class="card mt-3">
                    <div class="card-body">
                        <h5 class="col-12 text-uppercase bg-light py-1">
                            History
                        </h5>
                        <history-table :loan-product-id="id" />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <a
                                v-if="editMode"
                                :href="previewUrl"
                                class="btn btn-primary btn-static waves-effect waves-light m-1"
                                target="_blank"
                                @click="onPreviewButtonClick"
                            >
                                <i class="far fa-eye" />
                                Preview
                            </a>

                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="loanProduct"
                            :slug="loanProduct.slug"
                            :options="options"
                            :slug-error="slugError"
                            :featured-image="loanProduct.featuredImageId"
                            get-tags-function-name="productTags/index"
                            :tags="loanProduct.tags"
                            @meta-change="onMetaChange"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-featured-image-change="onFeaturedImageChange"
                            @on-tags-change="onTagsChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required } from 'vuelidate/lib/validators';
import { frontendUrl } from '@/config';
import AddEditForm from '@/components/loan-products/AddEditForm';
import ItemInformation from '@/components/elements/ItemInformation';
import HistoryTable from '@/components/loan-products/HistoryTable';

export default {
    page: {
        title: 'Manage Product'
    },

    validations: {
        loanProduct: {
            name: {
                required
            },
            sku: {
                required
            },
            slug: {
                required
            },
            description: {
                required
            },
            loanPeriod: {
                required,
                hasToBePositiveNumber: v => {
                    v = parseInt(v);

                    if (isNaN(v)) {
                        return false;
                    }

                    return v > 0;
                }
            },
            disclaimerId: {
                required
            },
            stockAmount: {
                required,
                canNotBeNegative: v => v >= 0
            }
        }
    },

    components: {
        AddEditForm,
        HistoryTable,
        ItemInformation
    },

    data() {
        const { id = null, loanShopId } = this.$route.params;

        return {
            id,
            editMode: !!id,
            loanProduct: {
                sku: Math.random()
                    .toString(36)
                    .substring(2, 8)
                    .toUpperCase(),
                meta: {},
                tags: [],
                slug: ''
            },
            loanShop: {},
            informationKey: 0,
            serverErrors: [],
            loanProductValueStringify: '',
            loanShopId
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                meta: true,
                featuredImage: true,
                tags: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        previewUrl() {
            return `${frontendUrl}/loan-products/${this.loanProduct.slug}`;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.loanProduct,
                this.serverErrors,
                'slug'
            );
        },

        title() {
            return `${this.editMode ? 'Edit' : 'Add'} ${
                this.loanShop.name
            }'s Loan Product`;
        },

        items() {
            return [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Loan Shops',
                    to: '/loan-shops'
                },
                {
                    text: this.loanShop.name,
                    to: `/loan-shops/${this.loanShop.id}/edit`
                },
                {
                    text: `${this.loanShop.name}'s Loan Products`,
                    to: `/loan-shops/${this.loanShop.id}/loan-products`
                },
                {
                    text: `${this.editMode ? 'Edit' : 'Add'} Loan Product`,
                    active: true
                }
            ];
        },

        options() {
            return {
                slugBasicUrl: 'loan-products'
            };
        }
    },

    async created() {
        try {
            this.loanShop = await this.getLoanShop(this.loanShopId);
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }

        if (this.editMode) {
            try {
                this.loanProduct = await this.getLoanProduct(this.id);

                this.loanProductValueStringify = JSON.stringify(
                    this.loanProduct
                );
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            getLoanShop: 'loanShops/show',
            addLoanProduct: 'loanProducts/store',
            deleteLoanProduct: 'loanProducts/destroy',
            getLoanProduct: 'loanProducts/show',
            updateLoanProduct: 'loanProducts/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const tags = this.loanProduct.tags.map(tag => tag.id);

            const data = {
                ...this.loanProduct,
                tags
            };

            try {
                if (!this.editMode) {
                    data.loanShopId = this.loanShopId;

                    const added = await this.addLoanProduct(data);

                    this.$router.push(
                        `/loan-shops/${this.loanShopId}/loan-products/${added.id}/edit`
                    );
                } else {
                    const { updatedAt } = await this.updateLoanProduct({
                        id: this.id,
                        data
                    });

                    this.loanProduct.updatedAt = updatedAt;
                    this.loanProductValueStringify = JSON.stringify(
                        this.loanProduct
                    );

                    this.$toaster('Product updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteLoanProduct(this.id);

                this.$router.push(
                    `/loan-shops/${this.loanShopId}/loan-products`
                );
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onPreviewButtonClick(e) {
            if (
                this.loanProductValueStringify !==
                JSON.stringify(this.loanProduct)
            ) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onMetaChange(meta) {
            this.loanProduct.meta = { ...meta };
        },

        onNameChange() {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(this.loanProduct.name);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.loanProduct.slug = slug;
        },

        onFeaturedImageChange(imageId) {
            this.loanProduct.featuredImageId = imageId;
        },

        onTagsChange(tags) {
            this.loanProduct.tags = tags;
            this.informationKey++;
        }
    }
};
</script>
